import { createSlice } from "@reduxjs/toolkit";
import {
  analyticsFilterDropdowns,
  analyticsGraphsJSON,
  getActivityThresholdData,
  getCustomerDetailData,
  getCustomerPNData,
  getCustomerQuoteActivityData,
  getCustomerQuoteGraphsData,
  getCustomerQuoteHistoryTrendData,
  getCustomerQuoteKPIData,
  getFullQuotesData,
  getGraphsList,
  getPartNumberList,
  updateActivityThreshold,
} from "./analyticsActions";
import { transformFilterOptions } from "../../../utils/helper";

export const initialState = {
  filterDropdowns: {},
  loading: false,
  graphsData: {},
  graphLoader: false,
  graphLoaders: {},
  graphsList: {},
  partNumberList: {},
  partNumberLoader: false,
  filterOptions: [],
  kpiLoading: false,
  kpiData: {},
  customerQuoteActivityData: {},
  quoteActivityLoader: false,
  quoteGraphsLoader: false,
  quoteGraphsData: {},
  thresholds: [],
  thresoldsUpdateLoader: false,
  thresoldsLoader: false,
  quoteHistoryTrendHistoryData: {},
  quoteHistoryTrendLoader: false,
  customerPNLoader: false,
  customePNData: {},
  fullQuoteLoader: false,
  fullQuoteData: {},
  customerDetailLoader: false,
  customerDetailData: null,
};

const analyticsSlice = createSlice({
  name: "analytics",
  initialState,
  reducers: {
    clearAnalyticsState: (state) => {
      state.loading = false;
      state.filterDropdowns = {};
      state.graphLoader = false;
      state.graphLoaders = {};
      state.graphsData = {};
      state.graphsList = {};
      state.partNumberList = {};
      state.partNumberLoader = false;
      state.thresholds = [];
      state.thresoldsUpdateLoader = false;
      state.thresoldsLoader = false;
    },
    setGraphLoader: (state, action) => {
      state.graphLoader = action.payload;
    },
    clearGraphData: (state, action) => {
      const uuid = action.payload;
      if (uuid) {
        state.graphsData[uuid] = null;
      }
    },
    clearQuoteData: (state) => {
      state.kpiLoading = false;
      state.kpiData = {};
      state.customerQuoteActivityData = {};
      state.quoteActivityLoader = false;
      state.quoteGraphsLoader = false;
      state.quoteGraphsData = {};
      state.quoteHistoryTrendHistoryData = {};
      state.quoteHistoryTrendLoader = false;
      state.customerPNLoader = false;
      state.customePNData = {};
      state.fullQuoteLoader = false;
      state.fullQuoteData = {};
      state.customerDetailLoader = false;
      state.customerDetailData = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // graph list
      .addCase(getGraphsList.pending, (state) => {
        state.loading = true;
      })
      .addCase(getGraphsList.fulfilled, (state, action) => {
        state.loading = false;
        state.graphsList = action.payload?.data;
      })
      .addCase(getGraphsList.rejected, (state) => {
        state.loading = false;
      })
      // filters
      .addCase(analyticsFilterDropdowns.pending, (state) => {
        state.loading = true;
      })
      .addCase(analyticsFilterDropdowns.fulfilled, (state, action) => {
        state.loading = false;
        const data = action.payload?.data;
        state.filterDropdowns =
          data && Object.keys(data).length ? transformFilterOptions(data) : {};
      })
      .addCase(analyticsFilterDropdowns.rejected, (state, action) => {
        state.loading = false;
      })
      // graphs json
      .addCase(analyticsGraphsJSON.pending, (state, action) => {
        // state.graphLoader = true;
        // state.graphsData = {};
        const uuid = action.meta.arg.uuid;
        state.graphLoaders[uuid] = true;
      })
      .addCase(analyticsGraphsJSON.fulfilled, (state, action) => {
        // state.graphLoader = false;
        // state.graphsData = action.payload?.data;
        const uuid = action.meta.arg.uuid;
        state.graphLoaders[uuid] = false;
        state.graphsData = {
          ...state.graphsData,
          [uuid]: action.payload?.data,
        };
      })
      .addCase(analyticsGraphsJSON.rejected, (state, action) => {
        // state.graphLoader = false;
        // state.graphsData = {};
        const uuid = action.meta.arg.uuid;
        state.graphLoaders[uuid] = false;
        state.graphsData[uuid] = null;
      })
      // part number list
      .addCase(getPartNumberList.pending, (state, action) => {
        state.partNumberLoader = true;
      })
      .addCase(getPartNumberList.fulfilled, (state, action) => {
        state.partNumberLoader = false;
        state.partNumberList = action.payload?.data;
      })
      .addCase(getPartNumberList.rejected, (state, action) => {
        state.partNumberLoader = false;
      })
      // customer quote kpi data
      .addCase(getCustomerQuoteKPIData.pending, (state) => {
        state.kpiLoading = true;
      })
      .addCase(getCustomerQuoteKPIData.fulfilled, (state, action) => {
        state.kpiLoading = false;
        state.kpiData = action.payload?.data || {};
      })
      .addCase(getCustomerQuoteKPIData.rejected, (state) => {
        state.kpiLoading = false;
      })
      // customer quote activity data
      .addCase(getCustomerQuoteActivityData.pending, (state) => {
        state.quoteActivityLoader = true;
      })
      .addCase(getCustomerQuoteActivityData.fulfilled, (state, action) => {
        state.quoteActivityLoader = false;
        state.customerQuoteActivityData = action.payload || {};
      })
      .addCase(getCustomerQuoteActivityData.rejected, (state) => {
        state.quoteActivityLoader = false;
        state.customerQuoteActivityData = {};
      })
      // customer quote graphs data
      .addCase(getCustomerQuoteGraphsData.pending, (state) => {
        state.quoteGraphsLoader = true;
      })
      .addCase(getCustomerQuoteGraphsData.fulfilled, (state, action) => {
        state.quoteGraphsLoader = false;
        state.quoteGraphsData = action.payload?.data || {};
      })
      .addCase(getCustomerQuoteGraphsData.rejected, (state) => {
        state.quoteGraphsLoader = false;
        state.quoteGraphsData = {};
      })
      // customer quote activity flags data
      .addCase(getActivityThresholdData.pending, (state) => {
        state.thresoldsLoader = true;
      })
      .addCase(getActivityThresholdData.fulfilled, (state, action) => {
        state.thresoldsLoader = false;
        state.thresholds = action.payload?.data || [];
      })
      .addCase(getActivityThresholdData.rejected, (state) => {
        state.thresoldsLoader = false;
        state.thresholds = [];
      })
      // customer quote activity flags data update
      .addCase(updateActivityThreshold.pending, (state) => {
        state.thresoldsUpdateLoader = true;
      })
      .addCase(updateActivityThreshold.fulfilled, (state, action) => {
        state.thresoldsUpdateLoader = false;
      })
      .addCase(updateActivityThreshold.rejected, (state) => {
        state.thresoldsUpdateLoader = false;
        state.thresholds = {};
      })
      // customer quote history trend data
      .addCase(getCustomerQuoteHistoryTrendData.pending, (state) => {
        state.quoteHistoryTrendLoader = true;
      })
      .addCase(getCustomerQuoteHistoryTrendData.fulfilled, (state, action) => {
        state.quoteHistoryTrendLoader = false;
        state.quoteHistoryTrendHistoryData = action.payload?.data || {};
      })
      .addCase(getCustomerQuoteHistoryTrendData.rejected, (state) => {
        state.quoteHistoryTrendLoader = false;
        state.quoteHistoryTrendHistoryData = {};
      })
      // customer quote pn  data
      .addCase(getCustomerPNData.pending, (state) => {
        state.customerPNLoader = true;
      })
      .addCase(getCustomerPNData.fulfilled, (state, action) => {
        state.customerPNLoader = false;
        state.customePNData = action.payload || {};
      })
      .addCase(getCustomerPNData.rejected, (state) => {
        state.customerPNLoader = false;
        state.customePNData = {};
      })
      // customer full quote data
      .addCase(getFullQuotesData.pending, (state) => {
        state.fullQuoteLoader = true;
      })
      .addCase(getFullQuotesData.fulfilled, (state, action) => {
        state.fullQuoteLoader = false;
        state.fullQuoteData = action.payload || {};
      })
      .addCase(getFullQuotesData.rejected, (state) => {
        state.fullQuoteLoader = false;
        state.fullQuoteData = {};
      })
      // customer detail (header card data by cmp_auto_key)
      .addCase(getCustomerDetailData.pending, (state) => {
        state.customerDetailLoader = true;
        state.customerDetailData = null;
      })
      .addCase(getCustomerDetailData.fulfilled, (state, action) => {
        state.customerDetailLoader = false;
        const row = action.payload?.data?.[0] || null;
        if (row) {
          state.customerDetailData = {
            name: row.COMPANY_NAME,
            region: row.REGION,
            lastQuoteDate: row.LastQuoteDate,
            activityFlag: row.ActivityFlag,
            employee: row.EMPLOYEE,
            cmp_auto_key: row.CMP_AUTO_KEY,
          };
        } else {
          state.customerDetailData = null;
        }
      })
      .addCase(getCustomerDetailData.rejected, (state) => {
        state.customerDetailLoader = false;
        state.customerDetailData = null;
      });
  },
});
export const {
  clearAnalyticsState,
  setGraphLoader,
  clearGraphData,
  clearQuoteData,
} = analyticsSlice.actions;
export default analyticsSlice.reducer;
